import React, { useState, useEffect, useRef, memo, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import QRCodeStyling from "qr-code-styler";
import { defaultLogo } from "../qrSettings/logotypeQr/svg";
import QrTemplate from "../../../components/qrTemplate/QrTemplate";
import { setLink } from "../../../redux/slices/getQrLinkSlice";
import { convertImageToBase64 } from "../../../hooks/helpers";
import { Button } from "react-bootstrap";
import { qrLogotype } from "../../../redux/slices/qrSettings";
import { DefaultLogoContext } from "../index";
import { WrapperQr } from "../generatorQR";
import useSaveVisualization from "../../../hooks/useSaveVisualization";
import useCurrentLocation from "../../../hooks/useCurrentLocation";
import { setIsTemplateApplystate } from "../../../redux/slices/modals";
import useTemplateServiceParams from "../../../hooks/useTemplateServiceParams";
import { getBase64Logo } from "../../../hooks/helpersThirdStep";
import { designTypes } from "../../../constants/common";

export default memo(function TemplateQr({ top = 0 }) {
  const {
    lang,
    getQrLinkSlice,
    qrSettings,
    qrFrames,
    qrOptions,
    locale,
    designType,
  } = useSelector((state) => ({
    lang: state.changeLang.language.stepThree.generator,
    getQrLinkSlice: state.getQrLinkSlice,
    qrSettings: state.qrSettings,
    qrFrames: state.qrFrames,
    qrOptions: state.qrOptions,
    locale: state.changeLang.change,
    designType: state.customizeQr.designType,
  }));

  const [url, setUrl] = useState("");
  let ref = useRef(null);
  let svgWrapperRef = useRef(null);
  const dispatch = useDispatch();
  const { defaultLogoData, setDefaultLogoData, defaultBackgroundImageData } =
    useContext(DefaultLogoContext);
  const { saveTemplateVisualisation } = useSaveVisualization();
  const { isTemplateService } = useCurrentLocation();
  const { applyTemplateUrl } = useTemplateServiceParams();

  const qrCode = new QRCodeStyling({
    width: 300,
    height: 300,
    type: "svg",
  });

  useEffect(() => {
    setUrl(getQrLinkSlice.link);
  }, [getQrLinkSlice.link]);

  useEffect(() => {
    let newUrl = new URL(window.location);
    let c = newUrl.searchParams.get("url");
    if (c === null) {
      setUrl(getQrLinkSlice.link);
    } else {
      dispatch(setLink(c));
    }
  }, [getQrLinkSlice.link]);

  useEffect(() => {
    if (qrSettings.logotype?.includes("/qr/logo/")) {
      convertImageToBase64(qrSettings.logotype).then((data) => {
        dispatch(qrLogotype(data));
      });
    } else if (isTemplateService) {
      getBase64Logo(qrSettings.logotype).then((data) =>
        setDefaultLogoData(data)
      );
    }
  }, [qrSettings.logotype]);

  useEffect(() => {
    const {
      patternColor,
      pattern,
      cornetsOuter,
      cornetsOuterColor,
      cornetsInterior,
      cornetsInteriorColor,
      logotype,
      logotypeSize,
      logotypeMargin,
      logotypeBackground,
      isSingleColorDots,
      gradientDots,
      isSingleColorExternalEye,
      gradientExternalEye,
      isSingleColorInternalEye,
      gradientInternalEye,
      isSingleColorBackground,
      gradientBackground,
      qrBackgroundColor,
      backgroundImageSize,
      backgroundImage,
    } = qrSettings;
    const getSvg =
      logotype && defaultLogo.find((findElem) => findElem.id === logotype);
    const getBgSvg =
      backgroundImage &&
      defaultLogo.find((findElem) => findElem.id === backgroundImage);
    let svg = ref.current !== null && ref.current.querySelector("svg");
    svg && svg.remove();
    const updateQrCode = async () => {
      qrCode.update({
        data: url,
        image: getSvg ? defaultLogoData : logotype,
        dotsOptions: {
          color: patternColor,
          type: pattern,
          gradient: isSingleColorDots ? null : gradientDots,
        },
        cornersSquareOptions: {
          color: cornetsOuterColor,
          type: cornetsOuter,
          gradient: isSingleColorExternalEye ? null : gradientExternalEye,
        },
        cornersDotOptions: {
          color: cornetsInteriorColor,
          type: cornetsInterior,
          gradient: isSingleColorInternalEye ? null : gradientInternalEye,
        },
        imageOptions: {
          imageSize: logotypeSize,
          margin: logotypeMargin,
          hideBackgroundDots: logotypeBackground,
        },
        backgroundOptions: {
          gradient:
            isSingleColorBackground === false
              ? gradientBackground
              : isSingleColorBackground === true
              ? {
                  colorStops: [
                    { offset: 0, color: qrBackgroundColor },
                    { offset: 1, color: qrBackgroundColor },
                  ],
                }
              : null,
        },
        qrOptions: {
          errorCorrectionLevel: qrOptions.errorCorrectionLevel,
        },
        ...(designType === designTypes.art && {
          maskOptions: {
            drawMask: true,
            image: getBgSvg ? defaultBackgroundImageData : backgroundImage,
            imageSize: backgroundImageSize,
          },
        }),
      });

      qrCode.append(ref.current);
    };
    updateQrCode();
  }, [
    url,
    qrSettings,
    qrFrames,
    qrOptions,
    defaultLogoData,
    defaultBackgroundImageData,
    designType,
  ]);

  const handleSaveQrTemplate = async () => {
    if (isTemplateService) {
      await saveTemplateVisualisation(applyTemplateUrl);
      dispatch(setIsTemplateApplystate(true));
    } else {
      await saveTemplateVisualisation();
      window.location.href = `${window.location.origin}${
        locale === "en" ? "" : `/${locale}`
      }/csv-import/`;
    }
  };

  return (
    <aside
      className="bg-white p-4 pb-3 sticky-lg-top rounded-1"
      style={{ top: top + "px", zIndex: 390 }}
    >
      <WrapperQr
        className="p-3 pt-2"
        isTransparent={qrSettings.patternBackgroundTransparent}
      >
        <QrTemplate wrapRef={svgWrapperRef} gRef={ref} />
      </WrapperQr>

      <div>
        <Button
          id="downloadQrCode"
          variant="primary"
          className="text-white w-100 fw-bold py-3 my-2"
          onClick={handleSaveQrTemplate}
        >
          {lang[9]}
        </Button>
      </div>
    </aside>
  );
});
